/**
 * The cart — qty steppers, per-item notes, "goes well with" suggestions
 * (drinks/desserts not yet in the cart, this device's favorites first) and the
 * checkout button.
 *
 *   CartPanel   the cart itself. The order screen docks it beside the menu on
 *               wide screens (tablet landscape, desktop).
 *   CartDrawer  (default) the same panel as a slide-over, for phones and for the
 *               staff cart on /menu.
 */
import React, { useMemo, useState } from 'react';
import { useOrdering } from './OrderingContext';
import { fmt$ } from './orderApi';
import { cartSuggestions, needsConfiguration, defaultConfig } from './orderHistory';
import { useOverlay } from './overlayHooks';

const cents$ = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export function CartPanel({ docked = false, onClose }) {
  const {
    isStaff, payEnv, cart, nameMap,
    setDrawerOpen, setCheckoutOpen, setMappingOpen,
    history, menuCategories, openItem,
  } = useOrdering();
  const [noteOpen, setNoteOpen] = useState(() => new Set());

  const suggestions = useMemo(
    () => cartSuggestions(cart.items, menuCategories, history, 3),
    [cart.items, menuCategories, history]
  );

  const isSandbox = payEnv !== 'production';
  const hasLegacyLines = cart.items.some(it => !it.catalogObjectId);

  const addSuggestion = (item) => {
    if (needsConfiguration(item)) { onClose?.(); openItem(item); return; }
    const cfg = defaultConfig(item);
    if (cfg) cart.addConfigured(cfg, 1);
  };

  const checkout = () => { setDrawerOpen(false); setCheckoutOpen(true); };
  const showNote = (key) => setNoteOpen(prev => new Set(prev).add(key));

  return (
    <div className={`ord-cart${docked ? ' ord-cart--docked' : ''}`}>
      <div className="ord-drawer__head">
        <h3>
          Your order
          {cart.count > 0 && <span className="ord-count">{cart.count}</span>}
          {isSandbox && <span className="ord-test-pill">TEST</span>}
        </h3>
        {!docked && <button className="ord-x" onClick={onClose} aria-label="Close">✕</button>}
      </div>

      {cart.items.length === 0 ? (
        <div className="ord-empty">
          <span aria-hidden="true">🛍</span>
          <p>Your cart is empty</p>
          <small>Tap <b>＋</b> on anything that looks good.</small>
        </div>
      ) : (
        <>
          <div className="ord-drawer__items">
            {cart.items.map(it => {
              const unit = Number(it.unitPrice ?? it.basePrice ?? it.price ?? 0);
              // Legacy (non-catalog) lines still show the fuzzy Square name check.
              const r = it.catalogObjectId ? null : nameMap.resolve(it.name);
              const options = [
                it.variationName && it.variationName !== 'Regular' ? it.variationName : null,
                ...(it.modifiers || []).map(m => m.name + (m.priceCents > 0 ? ` (+${cents$(m.priceCents)})` : '')),
              ].filter(Boolean);
              const noteShown = !!it.note || noteOpen.has(it.key);
              return (
                <div key={it.key} className="ord-line">
                  <div className="ord-line__top">
                    <span className="ord-line__name">{it.name}</span>
                    <span className="ord-line__price">{fmt$(it.qty * unit)}</span>
                  </div>
                  {options.length > 0 && <span className="ord-line__opt">{options.join(' · ')}</span>}
                  {r && r.source !== 'none' && r.squareName !== it.name && (
                    <span className="ord-line__sq" title={`Will appear in Square as “${r.squareName}” (${r.source})`}>
                      → Square: {r.squareName}{r.source === 'auto' ? ' (auto)' : ''}
                    </span>
                  )}
                  {r && r.source === 'none' && (
                    <span className="ord-line__sq ord-line__sq--warn" title="No Square match — this item can't be ordered until it's mapped">
                      ⚠ No Square match
                    </span>
                  )}
                  <div className="ord-line__row">
                    <div className="ord-qty">
                      <button onClick={() => cart.dec(it.key)} aria-label={it.qty === 1 ? `Remove ${it.name}` : `One less ${it.name}`}>{it.qty === 1 ? '🗑' : '−'}</button>
                      <span>{it.qty}</span>
                      <button onClick={() => cart.inc(it.key)} aria-label={`One more ${it.name}`}>+</button>
                    </div>
                    {!noteShown && (
                      <button className="ord-linkbtn" onClick={() => showNote(it.key)}>Add note</button>
                    )}
                  </div>
                  {noteShown && (
                    <input
                      className="ord-line__note"
                      placeholder="Note for the kitchen (extra spicy, no onion…)"
                      value={it.note}
                      maxLength={200}
                      autoFocus={!it.note}
                      onChange={e => cart.setNote(it.key, e.target.value)}
                    />
                  )}
                </div>
              );
            })}

            {suggestions.length > 0 && (
              <div className="osc-suggest">
                <span className="osc-suggest__label">Goes well with</span>
                <div className="osc-suggest__row">
                  {suggestions.map(s => (
                    <button key={s.id} className="osc-suggest__item" onClick={() => addSuggestion(s)}>
                      <span className="osc-suggest__name">{s.name}</span>
                      <span className="osc-suggest__price">
                        ＋ {cents$((s.variations || []).find(v => !v.soldOut)?.priceCents ?? s.basePriceCents)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="ord-drawer__foot">
            {isStaff && hasLegacyLines && (
              <button className="ord-map-link" onClick={() => setMappingOpen(true)}>
                ⚙ Fix Square item names
              </button>
            )}
            <div className="ord-subtotal">
              <span>Subtotal</span>
              <strong>{fmt$(cart.subtotal)}</strong>
            </div>
            <p className="ord-note">
              {isSandbox ? 'Test mode — no real charge. Tax is added at checkout.' : 'Tax is added at checkout.'}
              <button className="ord-linkbtn ord-linkbtn--quiet" onClick={() => cart.clear()}>Clear cart</button>
            </p>
            <button className="ord-btn ord-btn--primary ord-btn--block" onClick={checkout}>
              <span>Checkout</span>
              <span>{fmt$(cart.subtotal)}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function CartDrawer() {
  const { canOrder, drawerOpen, setDrawerOpen } = useOrdering();
  const open = !!(canOrder && drawerOpen);
  const close = () => setDrawerOpen(false);
  useOverlay(open, close);

  if (!open) return null;

  return (
    <div className="ord-overlay" onClick={close}>
      <aside className="ord-drawer" onClick={e => e.stopPropagation()} role="dialog" aria-label="Your order">
        <CartPanel onClose={close} />
      </aside>
    </div>
  );
}
